import { ChangeEvent, FC, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { cloneDeep, isEqual, sortBy } from 'lodash';
import { Button, FormCheckbox, FormInput } from '@praktio/common-ui';

import { RightPaneHeader } from '../../common/RightPane/RightPane';
import { RootState } from '../../../ducks/store';
import { useParams, useSearchParams } from 'react-router-dom';
import { Roles } from '../../../utils/constants';

import './ManagerLearnerAssignments.scss';
import { updateManagerLearnerAssignments } from '../../../ducks/teams';

interface IProps {
  closeButtonAction: () => void;
}

const All = 'all';
const Some = 'some';
const RadioChoices = [
  { id: All, label: 'All learners in this team', value: All },
  { id: Some, label: 'Select learner(s) in this team', value: Some },
];

const ManagerLearnerAssignments: FC<IProps> = ({ closeButtonAction }) => {
  const params = useParams();
  const [searchParams] = useSearchParams();
  const userId = searchParams.get('userId') || undefined;
  const teamId = params.teamId as string;
  const prevInitialAssignments = useRef<IManagerLearnerAssignment[]>([]);

  const dispatch = useDispatch();
  const { learners, manager, initialAssignments } = useSelector((state: RootState) => {
    const { teams } = state;
    const { current: team } = teams;
    const learners = sortBy(
      team?.users?.filter((u: IUser) => u.roles?.find((r) => r.role === Roles.Learner)),
      'lastName',
    );

    const manager = team?.users?.find((u: IUser) => u._id === userId);
    const learnerAssignments =
      manager?.learnerAssignments?.filter((a) => a.teamId === teamId) || [];
    return { team, manager, learners, initialAssignments: learnerAssignments };
  });

  const [savedChanges, setSavedChanges] = useState(false);
  const [learnerAssignments, setLearnerAssignments] = useState(initialAssignments);
  const [learnerRadioValue, setLearnerRadioValue] = useState(
    initialAssignments?.length ? Some : All,
  );

  useEffect(() => {
    if (!isEqual(prevInitialAssignments.current, initialAssignments)) {
      setLearnerAssignments(initialAssignments);
      setLearnerRadioValue(initialAssignments?.length ? Some : All);
    }

    prevInitialAssignments.current = initialAssignments;
  }, [initialAssignments]);

  const handleSubmit = async (e?: React.MouseEvent<HTMLElement>) => {
    e?.preventDefault();
    try {
      if (userId) {
        await dispatch(updateManagerLearnerAssignments(teamId, userId, learnerAssignments));
        setSavedChanges(true);
      }
    } catch (e) {
      console.error(`Unable to update learner assignments:`, e);
    }
  };

  const onRadioChange = (e: ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setLearnerRadioValue(e.target.value);
    if (value === All) {
      setLearnerAssignments([]);
    }
  };

  const onCheckChange = (e: ChangeEvent<HTMLInputElement>) => {
    const id = e.target.id;
    const updatedLearnerAssignments = cloneDeep(learnerAssignments);
    const index = updatedLearnerAssignments.findIndex(
      (a) => a.userId === id && a.teamId === teamId,
    );
    if (index > -1) {
      updatedLearnerAssignments.splice(index, 1);
    } else {
      updatedLearnerAssignments.push({ userId: id, teamId });
    }

    setLearnerAssignments(updatedLearnerAssignments);
  };

  const hasChanges = !isEqual(learnerAssignments, initialAssignments);

  return (
    <div className="ManagerLearnerAssignments">
      <RightPaneHeader
        headerText={`Learners in ${manager?.firstName} ${manager?.lastName}'s Report`}
        savedChanges={savedChanges}
        unsavedChanges={hasChanges}
        closeButtonAction={closeButtonAction}
        mainButton={
          <Button
            disabled={!hasChanges}
            onClick={async (event) => {
              await handleSubmit(event);
            }}
            form="learner-assignments-form"
            btnStyle="primary"
          >
            SAVE
          </Button>
        }
      />
      <div>
        Customize which learners display in the report for {manager?.firstName} {manager?.lastName}.
        If no learners are checked for “Select learner(s) in this team,” the selection will change
        to “All learners in this team” upon save.
      </div>
      <div className="ManagerLearnerAssignments-inputs">
        <FormInput
          type="radio"
          label="Select Learners"
          radioChoices={RadioChoices}
          inlineChoices
          value={learnerRadioValue}
          onChange={onRadioChange}
        />
        {learnerRadioValue === Some && (
          <div className="ManagerLearnerAssignments-checkboxes">
            {learners?.map((l) => {
              return (
                <FormCheckbox
                  key={l._id}
                  id={l._id as string}
                  label={`${l.firstName} ${l.lastName}`}
                  checked={
                    !!learnerAssignments?.some((a) => a.teamId === teamId && a.userId === l._id)
                  }
                  onChange={onCheckChange}
                />
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default ManagerLearnerAssignments;
